import { call, put, select, takeLatest } from 'redux-saga/effects'

import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import { buildSearchEndpoint } from './utils/contactsApi'
import { mapApiRowsToItems } from './mappers/contactsMapper'

import {
  EMPTY_PAGINATOR,
  normalizePaginator,
  safeT,
  extractApiMessage,
  extractApiError
} from './utils/contactsSagasUtils'

import {
  fetchListSuccess,
  fetchListFailure,
  fetchListRequest,
  actionRequest,
  actionSuccess,
  actionFailure,
  patchItemByContractId,
  markContractActionAt,
} from './contactsSlice'

import { selectContactsToolsState } from './contactsSelectors'

export const contactsActions = {
  FETCH_CONTACT_CONTRACTS_LIST: 'CONTACT_CONTRACTS_TOOLS/FETCH_LIST',
  SIGN_NET: 'CONTACT_CONTRACTS_TOOLS/SIGN_NET',
  SIGN_ABANDON: 'CONTACT_CONTRACTS_TOOLS/SIGN_ABANDON',
  UPDATE_PROVIDER_EXTERNAL_ID: 'CONTACT_CONTRACTS_TOOLS/UPDATE_PROVIDER_EXTERNAL_ID',
  RESYNC_ZOHO: 'CONTACT_CONTRACTS_TOOLS/RESYNC_ZOHO',
  RECONCILE_MCP_SALES: 'CONTACT_CONTRACTS_TOOLS/RECONCILE_MCP_SALES',
}

function* fetchContactContractsSaga({ enqueue, t }) {
  try {
    yield put(fetchListRequest())

    const { extraRoutes } = yield select(selectAuthData)
    const searchUrl = extraRoutes?.devTools?.contactContractsSearch
    if (!searchUrl) {
      const msg = safeT(
        t,
        'contacts.errors.missing_search_url',
        'Configuration error: missing Search URL',
      )
      yield put(fetchListFailure(msg))
      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.ERROR,
          message: msg,
        })
      }
      return
    }

    const { query, cursor, country } = yield select(selectContactsToolsState)

    const endpoint = buildSearchEndpoint(searchUrl, {
      country: country || 'all',
      cursor: cursor || null,
      term: query || '',
    })

    const result = yield call(callApi, { endpoint, showErrorSnackbar: false })

    const paginator = result?.contacts || null
    const items = paginator ? mapApiRowsToItems(paginator) : []

    yield put(
      fetchListSuccess({
        items,
        paginator: normalizePaginator(paginator),
      }),
    )
  } catch (error) {
    const { status, message } = extractApiError(error)

    if (status === 404) {
      yield put(fetchListSuccess({ items: [], paginator: EMPTY_PAGINATOR }))
      return
    }

    if (status === 422) {
      yield put(fetchListSuccess({ items: [], paginator: EMPTY_PAGINATOR }))
      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.WARNING,
          message: safeT(
            t,
            'contacts.errors.search_term_too_short',
            'Please enter a search term',
          ),
        })
      }
      return
    }
    const base = safeT(t, 'contacts.errors.fetch_list', 'Error fetching contacts')
    const finalMessage = message ? `${base} — ${message}` : base
    yield put(fetchListFailure(finalMessage))

    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: finalMessage,
      })
    }
  }
}

function* resyncZohoSaga({ payload, enqueue, t }) {
  const { contactId, contractId, contractType } = payload || {}
  const loadingKey = `resyncZoho:${contractId ?? 'unknown'}`

  try {
    yield put(actionRequest(loadingKey))
    const { extraRoutes } = yield select(selectAuthData)
    const url = extraRoutes?.devTools?.contactContractsReSync
    if (!url) {
      yield put(actionFailure())

      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.ERROR,
          message: safeT(
            t,
            'contacts.errors.missing_resync_url',
            'Configuration error: missing Re-sync URL',
          ),
        })
      }
      return
    }
    const result = yield call(callApi, {
      endpoint: url,
      method: 'POST',
      payload: {
        contact_id: Number(contactId),
        contract_id: Number(contractId),
        contract_type: String(contractType),
      },
      showErrorSnackbar: false,
    })
    yield put(actionSuccess())
    yield put(markContractActionAt({ action: 'resyncZoho', contractId, at: Date.now() }))
    if (enqueue) {
      const base = safeT(t, 'contacts.actions.resync_zoho_success', 'Zoho resynchronized')
      const backendMsg = extractApiMessage(result)
      const message = backendMsg ? `${base} (${backendMsg})` : base
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message,
      })
    }
  } catch (error) {
    yield put(actionFailure())
    const { message } = extractApiError(error)
    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: `${safeT(t, 'contacts.errors.resync_zoho', 'Resync error')} — ${message}`,
      })
    }
  }
}

function* reconcileMcpSalesSaga({ payload, enqueue, t }) {
  const { contactZohoId } = payload || {}
  const loadingKey = `reconcileMcp:${contactZohoId ?? 'unknown'}`

  try {
    yield put(actionRequest(loadingKey))
    const { extraRoutes } = yield select(selectAuthData)
    const url = extraRoutes?.devTools?.reconcileMcpSales
    if (!url) {
      yield put(actionFailure())

      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.ERROR,
          message: safeT(
            t,
            'contacts.errors.missing_reconcile_mcp_sales_url',
            'Configuration error: missing MCP reconcile URL',
          ),
        })
      }
      return
    }
    const result = yield call(callApi, {
      endpoint: url,
      method: 'POST',
      payload: { contact_zoho_id: String(contactZohoId) },
      showErrorSnackbar: false,
    })
    yield put(actionSuccess())
    if (enqueue) {
      const base = safeT(t, 'contacts.actions.reconcile_mcp_sales_success', 'MCP reconciliation triggered')
      const backendMsg = extractApiMessage(result)
      const message = backendMsg ? `${base} (${backendMsg})` : base
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message,
      })
    }
  } catch (error) {
    yield put(actionFailure())
    const { status, message } = extractApiError(error)
    if (enqueue) {
      const base = status === 404
        ? safeT(t, 'contacts.errors.reconcile_mcp_sales_not_found', 'No contact found for this Zoho id')
        : safeT(t, 'contacts.errors.reconcile_mcp_sales', 'MCP reconciliation error')
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: status === 404 ? base : `${base} — ${message}`,
      })
    }
  }
}

function* updateProviderExternalIdSaga({ payload, enqueue, t }) {
  const { contractId, providerExternalId, contractType } = payload || {}
  const loadingKey = `updatePeid:${contractId ?? 'unknown'}`

  try {
    yield put(actionRequest(loadingKey))

    const { extraRoutes } = yield select(selectAuthData)
    const url = extraRoutes?.devTools?.contractsUpdateProviderExternalId
    if (!url) {
      yield put(actionFailure())
      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.ERROR,
          message: safeT(
            t,
            'contacts.errors.missing_update_peid_url',
            'Configuration error: missing Update Provider External ID URL',
          ),
        })
      }
      return
    }
    yield call(callApi, {
      endpoint: url,
      method: 'POST',
      payload: {
        provider_external_id: providerExternalId,
        contract_id: Number(contractId),
        contract_type: contractType || null,
      },
      showErrorSnackbar: false,
    })

    yield put(actionSuccess())
    yield put(
      patchItemByContractId({
        contractId,
        patch: { providerExternalId },
      }),
    )

    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message: safeT(t, 'contacts.actions.update_peid_success', 'Provider external ID updated'),
      })
    }
  } catch (error) {
    yield put(actionFailure())
    const { message } = extractApiError(error)
    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: `${safeT(t, 'contacts.errors.update_peid', 'Update error')} — ${message}`,
      })
    }
  }
}

function* signAbandonSaga({ payload, enqueue, t, callback }) {
  const { contractId, reason } = payload || {}
  const loadingKey = `signAbandon:${contractId ?? 'unknown'}`

  try {
    yield put(actionRequest(loadingKey))

    const { extraRoutes } = yield select(selectAuthData)
    const url = extraRoutes?.retractContractUrl
    if (!url) {
      yield put(actionFailure())
      if (typeof callback === 'function') yield call(callback, false)
      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.ERROR,
          message: safeT(
            t,
            'contacts.errors.missing_sign_abandon_url',
            'Configuration error: missing Sign Abandon URL',
          ),
        })
      }
      return
    }
    const result = yield call(callApi, {
      endpoint: url.replace('*', String(contractId)),
      method: 'POST',
      payload: { reason },
      showErrorSnackbar: false,
    })

    yield put(actionSuccess())
    yield put(markContractActionAt({ action: 'signAbandon', contractId, at: Date.now() }))

    if (typeof callback === 'function') yield call(callback, true)
    if (enqueue) {
      const base = safeT(t, 'contacts.actions.sign_to_abandon_success', 'Signed to Abandon')
      const backendMsg = extractApiMessage(result)
      const message = backendMsg ? `${base} (${backendMsg})` : base

      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message,
      })
    }

    yield put({ type: contactsActions.FETCH_CONTACT_CONTRACTS_LIST, enqueue, t })
  } catch (error) {
    yield put(actionFailure())
    if (typeof callback === 'function') yield call(callback, false)
    const { message } = extractApiError(error)
    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: `${safeT(t, 'contacts.errors.sign_to_abandon', 'Sign to Abandon error')} — ${message}`,
      })
    }
  }
}

function* signNetSaga({ payload, enqueue, t }) {
  const { contractId } = payload || {}
  const loadingKey = `signNet:${contractId ?? 'unknown'}`

  try {
    yield put(actionRequest(loadingKey))

    const { extraRoutes } = yield select(selectAuthData)
    const url = extraRoutes?.devTools?.contactContractsSignNet
    if (!url) {
      yield put(actionFailure())
      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.ERROR,
          message: safeT(
            t,
            'contacts.errors.missing_sign_net_url',
            'Configuration error: missing Sign NET URL'
          ),
        })
      }
      return
    }
    const result = yield call(callApi, {
      endpoint: url,
      method: 'POST',
      payload: null,
      asteriskReplacement: String(contractId),
      showErrorSnackbar: false,
    })

    yield put(actionSuccess())
    yield put(markContractActionAt({ action: 'signNet', contractId, at: Date.now() }))

    const updated = result?.contract
    if (updated?.id) {
      yield put(
        patchItemByContractId({
          contractId: updated.id,
          patch: {
            status: updated.status,
            contractUpdatedAt: updated.updated_at,
          },
        }),
      )
    }
    if (enqueue) {
      const base = safeT(t, 'contacts.actions.sign_to_net_success', 'Signed to NET')
      const backendMsg = extractApiMessage(result)
      const message = backendMsg ? `${base} (${backendMsg})` : base

      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message,
      })
    }
  } catch (error) {
    yield put(actionFailure())
    const { message } = extractApiError(error)
    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: `${safeT(t, 'contacts.errors.sign_to_net', 'Sign to NET error')} — ${message}`,
      })
    }
  }
}

export default function* contactsToolsSagas() {
  yield takeLatest(contactsActions.FETCH_CONTACT_CONTRACTS_LIST, fetchContactContractsSaga)
  yield takeLatest(contactsActions.SIGN_NET, signNetSaga)
  yield takeLatest(contactsActions.SIGN_ABANDON, signAbandonSaga)
  yield takeLatest(contactsActions.UPDATE_PROVIDER_EXTERNAL_ID, updateProviderExternalIdSaga)
  yield takeLatest(contactsActions.RESYNC_ZOHO, resyncZohoSaga)
  yield takeLatest(contactsActions.RECONCILE_MCP_SALES, reconcileMcpSalesSaga)
}
